import {
  Box,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { subDays, subMonths, subWeeks } from "date-fns";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { KeenAPI } from "../../../API/KeenAPI";
import { Products } from "../../../API/Product";
import { Users } from "../../../API/Users";
import CustomDatePicker from "../../../components/CustomDatePicker";
import MultipleSelectCheckmarks from "../../../components/MultipleSelectCheckmarks";
import { useAuth } from "../../../customHooks/useAuth";
import { TDate } from "../../../TDate";
import { applicationMap, periodValues, userTypes } from "../../Accounts/utils";
import {
  AnalyticsFilter_reducer,
  initialState,
} from "./AnalyticsFilter.reducer";

type AnalyticsFilterType = {
  setData?: Function;
  onClearFilters?: Function;
  setIsLoading?: Function;
};

const AnalyticsFilter: React.FC<AnalyticsFilterType> = ({
  setData,
  onClearFilters,
  setIsLoading,
}) => {
  const productsAPI = useMemo(() => new Products(), []);
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const accountIdsParam = searchParams.get("accountIds");
  const userAPI = useMemo(() => new Users(), []);
  const keenIOAPI = useMemo(
    () => new KeenAPI(auth.user.additionalCookies),
    [auth.user.additionalCookies]
  );

  const [state, dispatch] = useReducer(AnalyticsFilter_reducer, initialState);

  const onChangePeriod = useCallback((e: SelectChangeEvent) => {
    const value = e.target.value;
    dispatch({ type: "SET_PERIOD", value: value });
    let startDate: any = null;
    let today = new Date();
    switch (value) {
      case "1D":
        startDate = subDays(today, 1);
        break;
      case "1W":
        startDate = subWeeks(today, 1);
        break;
      case "1M":
        startDate = subMonths(today, 1);
        break;
      case "3":
        startDate = subMonths(today, 3);
        break;
      case "6":
        startDate = subMonths(today, 6);
        break;
      case "12":
        startDate = subMonths(today, 12);
        break;
      default:
        startDate = today;
        break;
    }
    dispatch({
      type: "SET_FROM_DATE",
      value: TDate.daysToMilliseconds(TDate.dateToDays(startDate)),
    });
    dispatch({
      type: "SET_TO_DATE",
      value: TDate.daysToMilliseconds(TDate.dateToDays(today)),
    });
    return { startDate, endDate: today };
  }, []);

  const onChangeFromDate = useCallback((dateInMiliseconds: any) => {
    const _date = TDate.daysToMilliseconds(TDate.dateToDays(dateInMiliseconds));
    dispatch({ type: "SET_FROM_DATE", value: _date });
  }, []);

  const onChangeToDate = useCallback((dateInMiliseconds: any) => {
    const _date = TDate.daysToMilliseconds(TDate.dateToDays(dateInMiliseconds));
    dispatch({ type: "SET_TO_DATE", value: _date });
  }, []);

  const applications = useMemo(() => {
    return Object.keys(applicationMap).map((key) => ({
      label: key,
      value: applicationMap[key],
      checked: false,
    }));
  }, []);

  const applicationChangeHandler = useCallback((values: any) => {
    dispatch({ type: "SET_APPLICATION_STATE", value: values });
  }, []);
  const levelChangeHandler = useCallback((values: any) => {
    dispatch({ type: "SET_LEVEL_STATE", value: values });
  }, []);

  const onOpenProductsFilter = useCallback(async () => {
    if (state.products.length <= 0) {
      const response = await productsAPI.getAllProducts();
      const _products = response.data.products.map((item: any) => ({
        label: item.codeName,
        value: item.id,
        checked: false,
      }));
      dispatch({ type: "SET_PRODUCTS", value: _products });
    }
  }, [state.products, productsAPI]);

  const onOpenOwnersFilter = useCallback(async () => {
    const response = await userAPI.getOwners();
    if (response.status === "OK") {
      const sellers = response.data.owners.map((element: any) => ({
        label: element,
        value: element,
        checked: false,
      }));
      dispatch({ type: "SET_OWNER", value: sellers });
    }
  }, [userAPI]);

  const clearFilter = useCallback(() => {
    dispatch({ type: "CLEAR_FILTER", value: null });
    setSearchParams({}, { replace: true });
    onClearFilters && onClearFilters();
    setIsFilteringByUserIds(false);
  }, [onClearFilters, setSearchParams]);

  const abortControllerRef = useRef<any>(null); // Referenza per l'AbortController

  const onFilter = useCallback(async () => {
    const userPropsToFetch: string[] = [
      "firstName",
      "lastName",
      "email",
      "product",
      "level",
      "owner",
      "group",
      "creationTime",
    ];
    const filters: any[] = [];
    filters.push({
      dimension: "active",
      segments: [true],
    });

    if (accountIdsParam) {
      const accountIds = accountIdsParam
        .split(",")
        .map(Number)
        .filter(Number.isFinite);
      if (accountIds.length > 0) {
        filters.push({
          dimension: "id",
          segments: accountIds,
        });
      }
    }

    if (state.levelState.length > 0) {
      filters.push({
        dimension: "level",
        segments: [...state.levelState],
      });
    }

    if (state.productState.length > 0) {
      filters.push({
        dimension: "product.id",
        segments: [...state.productState],
      });
    }

    if (state.ownerState.length > 0) {
      filters.push({
        dimension: "owner",
        segments: [...state.ownerState],
      });
    }

    try {
      setIsLoading && setIsLoading(true);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      let usersIds = await userAPI.getForFiltering(
        filters,
        abortControllerRef.current
      );
      usersIds = usersIds.data.ids;

      usersIds = await userAPI.fetchUserProperties(
        userPropsToFetch,
        usersIds,
        abortControllerRef.current
      );
      usersIds = usersIds.data.rows;
      usersIds.forEach((element: any) => {
        element.productId = element["product"]["id"];
      });

      const apps: any = {};
      Object.values(applicationMap).forEach((value: any) => {
        if (state.applicationState.includes(value)) {
          apps[value] = true;
        } else {
          apps[value] = false;
        }
      });

      const payload = {
        mode: "byUser",
        timeFrame: {
          start: state.fromDate,
          end: state.toDate,
        },
        product: state.productState,
        owners: state.ownerState,
        apps: apps,
        what: "users",
        users: usersIds,
        useTypes: state.levelState,
      };
      const response = await keenIOAPI.getUsageAnalytics(payload);
      if (setData) {
        setData(response);
      }
      setIsLoading && setIsLoading(false);
    } catch (error: any) {
      alert(`Something went wrong... ${error?.message}`);
      setIsLoading && setIsLoading(false);
    }
  }, [
    accountIdsParam,
    keenIOAPI,
    setData,
    setIsLoading,
    state.applicationState,
    state.fromDate,
    state.levelState,
    state.ownerState,
    state.productState,
    state.toDate,
    userAPI,
  ]);
  const [isFilteringByUserIds, setIsFilteringByUserIds] = useState(false);
  const filterByUserIds = useCallback(
    async (ids: number[], dates: { startDate: any; endDate: any }) => {
      setIsFilteringByUserIds(true);
      const userPropsToFetch: string[] = [
        "firstName",
        "lastName",
        "email",
        "product",
        "level",
        "owner",
        "group",
        "creationTime",
      ];
      const filters: any[] = [];
      filters.push({
        dimension: "id",
        segments: ids,
      });

      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        setIsLoading && setIsLoading(true);

        let usersIds = await userAPI.fetchUserProperties(
          userPropsToFetch,
          ids,
          abortControllerRef.current
        );
        usersIds = usersIds.data.rows;
        usersIds.forEach((element: any) => {
          element.productId = element["product"]["id"];
        });

        // set level filter based on fetched users
        const levels = new Set<string>();
        usersIds.forEach((user: any) => {
          levels.add(user.level);
        });
        // levelChangeHandler(Array.from(levels));

        // set owner filter based on fetched users
        const ownerSet = new Set<string>();
        usersIds.forEach((user: any) => {
          ownerSet.add(user.owner);
        });
        dispatch({ type: "SET_OWNER_STATE", value: Array.from(ownerSet) });

        // set product filter based on fetched users
        const productSet = new Set<number>();
        usersIds.forEach((user: any) => {
          productSet.add(user.productId);
        });
        dispatch({
          type: "SET_PRODUCT_STATE",
          value: Array.from(productSet),
        });

        // set application filter to all applications
        const apps: any = {};
        Object.values(applicationMap).forEach((value: any) => {
          if (initialState.applicationState.includes(value)) {
            apps[value] = true;
          } else {
            apps[value] = false;
          }
        });
        const payload = {
          mode: "byUser",
          timeFrame: {
            start: dates.startDate,
            end: dates.endDate,
          },
          product: Array.from(productSet),
          owners: Array.from(ownerSet),
          apps: apps,
          what: "users",
          users: usersIds,
          useTypes: Array.from(levels),
        };
        const response = await keenIOAPI.getUsageAnalytics(payload);
        if (setData) {
          setData(response);
        }
        setIsLoading && setIsLoading(false);
      } catch (error: any) {
        if (
          axios.isCancel?.(error) ||
          error?.code === "ERR_CANCELED" ||
          error?.name === "CanceledError"
        ) {
          // Cancellazione voluta: esci silenziosamente
          return;
        }
        alert(`Something went wrong... ${error?.message}`);
      } finally {
        setIsLoading && setIsLoading(false);
      }
    },
    [keenIOAPI, setData, setIsLoading, userAPI]
  );

  const filterByUserIdsRef = useRef(filterByUserIds);

  useEffect(() => {
    filterByUserIdsRef.current = filterByUserIds;
  }, [filterByUserIds]);

  useEffect(() => {
    if (!accountIdsParam) return;
    const accountIds = accountIdsParam
      .split(",")
      .map(Number)
      .filter(Number.isFinite);
    if (accountIds.length === 0) return;
    const dates = onChangePeriod({
      target: { value: "1M" },
    } as SelectChangeEvent);
    filterByUserIdsRef.current(accountIds, dates);
  }, [accountIdsParam, onChangePeriod]);

  const filterByUserIdsCallback = useCallback(
    (dates: any) => {
      if (!accountIdsParam) return;

      const accountIds = accountIdsParam
        .split(",")
        .map(Number)
        .filter(Number.isFinite);

      if (accountIds.length === 0) return;
      filterByUserIdsRef.current(accountIds, dates);
    },
    [accountIdsParam]
  );

  useEffect(() => {
    console.log("accountIdsParam changed:", accountIdsParam);
  }, [accountIdsParam]);
  useEffect(() => {
    console.log("additionalCookies changed", auth.user.additionalCookies);
  }, [auth.user.additionalCookies]);

  useEffect(() => {
    console.log("filterByUserIds identity changed");
  }, [filterByUserIds]);

  return (
    <Box display={"flex"} flexDirection={"column"} gap={1} width={"100%"}>
      <Box
        boxShadow={2}
        borderRadius={1}
        p={1}
        gap={1}
        display={"flex"}
        flexDirection={"column"}
      >
        <Typography>Period</Typography>
        <Select
          color="secondary"
          size="small"
          value={state.period}
          onChange={onChangePeriod}
        >
          {periodValues.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              <Typography>{item.label}</Typography>
            </MenuItem>
          ))}
        </Select>
        <CustomDatePicker
          label="From"
          value={state.fromDate}
          onChange={onChangeFromDate}
          dateHelperText={true}
        />
        <CustomDatePicker
          label="To"
          value={state.toDate}
          onChange={onChangeToDate}
          dateHelperText={true}
        />
      </Box>

      {!isFilteringByUserIds && (
        <Box
          boxShadow={2}
          borderRadius={1}
          p={1}
          gap={1}
          display={"flex"}
          flexDirection={"column"}
        >
          <Typography>Application</Typography>
          <MultipleSelectCheckmarks
            fullWidth
            data={applications}
            values={state.applicationState}
            onChangeValues={applicationChangeHandler}
            label={"Applications"}
          />
        </Box>
      )}

      {!isFilteringByUserIds && (
        <Box
          boxShadow={2}
          borderRadius={1}
          p={1}
          gap={1}
          display={"flex"}
          flexDirection={"column"}
        >
          <Typography>Type</Typography>
          <MultipleSelectCheckmarks
            fullWidth
            data={userTypes}
            values={state.levelState}
            onChangeValues={levelChangeHandler}
            label={"Type"}
          />
        </Box>
      )}

      {!isFilteringByUserIds && (
        <Box
          boxShadow={2}
          borderRadius={1}
          p={1}
          gap={1}
          display={"flex"}
          flexDirection={"column"}
        >
          <Typography>Products</Typography>
          <MultipleSelectCheckmarks
            data={state.products}
            fullWidth
            values={state.productState}
            onChangeValues={(values: any) =>
              dispatch({ type: "SET_PRODUCT_STATE", value: values })
            }
            onOpen={onOpenProductsFilter}
            label={"Products"}
          />
        </Box>
      )}

      {!isFilteringByUserIds && (
        <Box
          boxShadow={2}
          borderRadius={1}
          p={1}
          gap={1}
          display={"flex"}
          flexDirection={"column"}
        >
          <Typography>Owner</Typography>
          <MultipleSelectCheckmarks
            fullWidth
            data={state.owner}
            values={state.ownerState}
            onChangeValues={(values: any) =>
              dispatch({ type: "SET_OWNER_STATE", value: values })
            }
            onOpen={onOpenOwnersFilter}
            label={"Owners"}
          />
        </Box>
      )}

      <Box display={"flex"} gap={1}>
        <Button
          onClick={() => {
            if (!isFilteringByUserIds) {
              onFilter();
            } else {
              filterByUserIdsCallback({
                startDate: state.fromDate,
                endDate: state.toDate,
              });
            }
          }}
          color="secondary"
        >
          Filter
        </Button>
        <Button onClick={clearFilter}>Clear filter</Button>
      </Box>
    </Box>
  );
};

export default AnalyticsFilter;
